// mvp-returns §9.4 / Section 15 — Damaged Inventory DETAIL page.
// Loads one DamagedInventory row, surfaces item details + warehouse overview +
// damage description + evidence photos + audit history, and exposes the
// backend-supported actions (dispose / change category to RESTOCK_GOOD).

import {
  ActionIcon, Anchor, Box, Button, Divider, Grid, Group, Image, Modal, Select, SimpleGrid, Skeleton,
  Stack, Text, Textarea, UnstyledButton
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ArrowLeftIcon, CaretLeftIcon, CaretRightIcon, ImageIcon } from "@phosphor-icons/react";
import { ConfirmNote, DisabledReason, HelpButtonGroup, HelpIcon, nextStepToast, useHelpContext } from "@dehub/theme";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { DamagedInventoryApi } from "../../../apis";
import type { FinalDisposition } from "../../../apis/damaged-inventory";
import { DeliveryActionButton } from "../../../components/delivery/action-controls";
import { Dot, DotPill } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { DetailCard, SideRow } from "../../../components/return/detail-card";
import { useReturnActions } from "../../../components/return/return-actions";
import { ListState } from "../../../components/ui";
import { dateFormat } from "../../../utils/date";
import { message } from "../../../utils/message";
import { BUCKET_TAB_META, bucketLabel, bucketTone, dispositionLabel, dispositionOptions } from "../index";

/** @dehub/help key namespaces (spec 12 §0); the disposal toast is shared with the list. */
const HELP = "order.damaged-inventory-detail";
const HELP_LIST = "order.damaged-inventory-list";

export function DamagedInventoryDetailPage (){
  const { t } = useTranslation(["inbound", "common"]);
  const { id = "" } = useParams<{ id: string }>();
  const { permissionState } = useReturnActions();
  // dispose + move-bucket both need RETURN_SET_INVENTORY_IMPACT (BE damaged-inventory/*).
  const perm = permissionState("inventoryImpact");
  const mutateState = { allowed: perm.enabled, reason: perm.reason };

  const { data, isLoading, error, mutate } = useSWR(
    id ? ["damaged-inventory.get", id] : null,
    () => DamagedInventoryApi.get(id),
    { revalidateOnFocus: false }
  );

  // @dehub/help: the disposal state drives the drawer's «Одоо» step (R15 flow) and the status tab.
  const { set: setHelpContext } = useHelpContext();
  const helpStatus = data ? (data.disposedAt ? "DISPOSED" : "PENDING") : undefined;
  useEffect(() => {
    setHelpContext({ status: helpStatus });
  }, [helpStatus, setHelpContext]);

  // Warehouse overview (category unit counts) — one shared turnover call.
  const { data: turnover } = useSWR(
    ["damaged-inventory.turnover"],
    () => DamagedInventoryApi.turnover(),
    { revalidateOnFocus: false }
  );

  // ── Disposal / move state ──
  const [disposeOpen, disposeH] = useDisclosure(false);
  const [moveOpen, moveH] = useDisclosure(false);
  const [disposition, setDisposition] = useState<FinalDisposition>("DESTROYED");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // ── Photo gallery ──
  const [gallery, setGallery] = useState<string[]>([]);
  const [gIdx, setGIdx] = useState(0);
  const [galleryOpen, galleryH] = useDisclosure(false);
  const openGallery = (photos: string[], start: number) => {
    setGallery(photos);
    setGIdx(start);
    galleryH.open();
  };
  const gPrev = () => setGIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const gNext = () => setGIdx((i) => (i + 1) % gallery.length);

  const openDispose = (preset: FinalDisposition) => {
    setDisposition(preset);
    setNotes("");
    disposeH.open();
  };
  const openMove = () => {
    setNotes("");
    moveH.open();
  };

  const confirmDispose = async () => {
    try {
      setBusy(true);
      await DamagedInventoryApi.dispose(id, { finalDisposition: disposition, disposalNotes: notes || null });
      nextStepToast(`${HELP_LIST}.toast.dispose`, { fallback: { title: t("inbound:damaged.detail.disposeSuccess") } });
      disposeH.close();
      mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmMove = async () => {
    try {
      setBusy(true);
      await DamagedInventoryApi.moveBucket(id, { inventoryImpact: "RESTOCK_GOOD", notes: notes || null });
      nextStepToast(`${HELP}.toast.move`, { fallback: { title: t("inbound:damaged.detail.moveSuccess") } });
      moveH.close();
      mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const row = data;
  // O165 — the title is the source return number (never a UUID fragment); the product name is the line.
  const titleText = row?.returnNumber ?? t("inbound:damaged.detail.titleFallback");
  const breadcrumb = [
    { to: "/", label: t("inbound:damaged.breadcrumb.home") },
    { to: "/returns", label: t("inbound:damaged.breadcrumb.returns") },
    { to: "/damaged-inventory", label: t("inbound:damaged.breadcrumb.damagedStock") },
    { label: titleText }
  ];

  const isDisposed = !!row?.disposedAt;
  const photos = row?.damageEvidencePhotos ?? [];
  const buckets = turnover?.buckets ?? [];
  const currentBucketStat = buckets.find((b) => b.inventoryImpact === row?.inventoryImpact) ?? null;
  const receiver = row?.receivedByUserName ?? "-";
  const disposer = row?.disposedByUserName ?? "-";

  return (
    <PageLayout
      title={titleText}
      subTitle={row ? (row.productName ?? row.sku ?? "-") : undefined}
      breadcrumb={breadcrumb}
      loading={isLoading && !data}
      extra={row ? [
        <DotPill key="bucket" tone={bucketTone(row.inventoryImpact)}>{bucketLabel(t, row.inventoryImpact)}</DotPill>
      ] : undefined}
    >
      <Stack gap="md">
        <Group>
          <Button variant="default" leftSection={<ArrowLeftIcon size={16} />} component={Link} to="/damaged-inventory">
            {t("inbound:damaged.detail.backToStock")}
          </Button>
        </Group>

        {error && !data ? (
          <ListState
            state="error"
            title={t("inbound:damaged.detail.loadErrorTitle")}
            description={t("inbound:damaged.detail.loadError")}
            onRetry={() => mutate()}
            errorCode={(error as { statusCode?: number })?.statusCode}
          />
        ) : null}

        {data && row ? (
          <Grid>
            {/* ── Main column ── */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                <DetailCard title={t("inbound:damaged.detail.itemInfo")} helpKey={`${HELP}.section.item-info`}>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
                    <SideRow label={t("inbound:damaged.detail.item")} value={row.productName ?? row.sku} />
                    <SideRow label="SKU" value={row.sku} mono />
                    {/* The BE stores no variant name for these rows, so the field reads «-» rather than an id. */}
                    <SideRow label={t("inbound:damaged.detail.variant")} value={null} />
                    <SideRow label={t("inbound:damaged.detail.quantity")} value={t("inbound:damaged.pcs", { count: row.quantity })} mono />
                    <SideRow label={t("inbound:damaged.detail.bucket")} value={bucketLabel(t, row.inventoryImpact)} />
                    <SideRow label={t("inbound:damaged.detail.location")} value={row.bucketLocationCode} />
                    <SideRow
                      label={t("inbound:damaged.detail.sourceOrder")}
                      value={row.returnRequestId && row.returnNumber ? (
                        <Anchor component={Link} to={`/returns/${row.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no">
                          {row.returnNumber}
                        </Anchor>
                      ) : null}
                    />
                    <SideRow label={t("inbound:damaged.detail.received")} value={dateFormat(row.receivedAt)} mono />
                    <SideRow label={t("inbound:damaged.detail.receiver")} value={row.receivedByUserName} />
                  </SimpleGrid>
                </DetailCard>

                <DetailCard title={t("inbound:damaged.detail.damageDesc")} helpKey={`${HELP}.section.damage`}>
                  <Text size="sm">{row.damageDescription || "-"}</Text>
                </DetailCard>

                <DetailCard title={t("inbound:damaged.detail.photoEvidence")} helpKey={`${HELP}.section.photos`}>
                  {photos.length ? (
                    <Group gap="md" wrap="wrap">
                      {photos.map((url, i) => (
                        <UnstyledButton key={url} onClick={() => openGallery(photos, i)} aria-label={t("inbound:damaged.detail.zoom")}>
                          <Image src={url} w={120} h={90} radius="md" fit="cover" fallbackSrc="https://placehold.co/240x180?text=No+image" />
                        </UnstyledButton>
                      ))}
                    </Group>
                  ) : (
                    <Group gap="xs">
                      <ImageIcon size={18} />
                      <Text size="sm" c="var(--nw-text-2)">{t("inbound:damaged.detail.noPhotos")}</Text>
                    </Group>
                  )}
                </DetailCard>

                <DetailCard title={t("inbound:damaged.detail.disposalActions")} helpKey={`${HELP}.section.disposal`}>
                  {isDisposed ? (
                    <Stack gap="xs">
                      <SideRow label={t("inbound:damaged.detail.disposal")} value={dispositionLabel(t, row.finalDisposition)} />
                      <SideRow label={t("inbound:damaged.detail.date")} value={dateFormat(row.disposedAt)} mono />
                      <SideRow label={t("inbound:damaged.detail.recordedBy")} value={row.disposedByUserName} />
                      {row.disposalNotes ? <SideRow label={t("inbound:damaged.detail.notes")} value={row.disposalNotes} /> : null}
                    </Stack>
                  ) : (
                    // @dehub/help: attached «?» (R2) + reason popover without the permission (R4).
                    <Group gap="sm" wrap="wrap">
                      <DisabledReason helpKey={`${HELP}.reason.action`} reason="PERM" when={!perm.enabled} position="bottom-start">
                        <HelpButtonGroup helpKey={`${HELP}.action.sell`}>
                          <DeliveryActionButton state={mutateState} variant="default" onClick={() => openDispose("RESOLD")}>
                            {t("inbound:damaged.detail.sellDamaged")}
                          </DeliveryActionButton>
                        </HelpButtonGroup>
                      </DisabledReason>
                      {row.inventoryImpact !== "RESTOCK_GOOD" ? (
                        <DisabledReason helpKey={`${HELP}.reason.action`} reason="PERM" when={!perm.enabled} position="bottom-start">
                          <HelpButtonGroup helpKey={`${HELP}.action.move`}>
                            <DeliveryActionButton state={mutateState} variant="default" onClick={openMove}>
                              {t("inbound:damaged.detail.moveToRestock")}
                            </DeliveryActionButton>
                          </HelpButtonGroup>
                        </DisabledReason>
                      ) : null}
                      <DisabledReason helpKey={`${HELP}.reason.action`} reason="PERM" when={!perm.enabled} position="bottom-start">
                        <HelpButtonGroup helpKey={`${HELP}.action.dispose`}>
                          <DeliveryActionButton data-help-id="damaged-dispose" state={mutateState} onClick={() => openDispose("DESTROYED")}>
                            {t("inbound:damaged.detail.markDisposed")}
                          </DeliveryActionButton>
                        </HelpButtonGroup>
                      </DisabledReason>
                    </Group>
                  )}
                </DetailCard>

                <DetailCard title={t("inbound:damaged.detail.auditHistory")} helpKey={`${HELP}.section.audit`}>
                  <Stack gap="sm">
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <DotPill tone="green">{t("inbound:damaged.detail.receivedBadge")}</DotPill>
                      <Box>
                        <Text size="sm">{dateFormat(row.receivedAt)} · {t("inbound:damaged.detail.keeper", { name: receiver })}</Text>
                        {row.returnRequestId && row.returnNumber ? (
                          <Anchor component={Link} to={`/returns/${row.returnRequestId}`} size="sm" ff="monospace" translate="no">{row.returnNumber}</Anchor>
                        ) : null}
                      </Box>
                    </Group>
                    {isDisposed ? (
                      <Group gap="sm" wrap="nowrap" align="flex-start">
                        <DotPill tone="green">{t("inbound:damaged.detail.disposedBadge")}</DotPill>
                        <Box>
                          <Text size="sm">{dateFormat(row.disposedAt)} · {dispositionLabel(t, row.finalDisposition)} · {disposer}</Text>
                          {row.disposalNotes ? <Text size="sm" c="var(--nw-text-2)">{row.disposalNotes}</Text> : null}
                        </Box>
                      </Group>
                    ) : (
                      <Group gap="sm" wrap="nowrap" align="flex-start">
                        <DotPill tone="grey">{t("inbound:damaged.detail.pendingBadge")}</DotPill>
                        <Text size="sm" c="var(--nw-text-2)">{t("inbound:damaged.detail.notDisposed")}</Text>
                      </Group>
                    )}
                  </Stack>
                </DetailCard>
              </Stack>
            </Grid.Col>

            {/* ── Sidebar: warehouse overview ── */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <DetailCard title={t("inbound:damaged.detail.warehouseOverview")} helpKey={`${HELP}.section.warehouse`}>
                {turnover ? (
                  <Stack gap="xs">
                    {BUCKET_TAB_META.map((b) => {
                      const stat = buckets.find((x) => x.inventoryImpact === b.value);
                      return (
                        <Group key={b.value} justify="space-between" wrap="nowrap">
                          <Group gap={8} wrap="nowrap">
                            <Dot tone={b.tone} />
                            <Text size="sm">{bucketLabel(t, b.value)}</Text>
                          </Group>
                          <Text size="sm" ff="monospace" fw={600}>{t("inbound:damaged.pcs", { count: stat?.activeQuantity ?? 0 })}</Text>
                        </Group>
                      );
                    })}
                    <Divider my="xs" />
                    {currentBucketStat ? (
                      <Stack gap={4}>
                        <Text size="sm" c="var(--nw-text-2)">{t("inbound:damaged.detail.thisBucketTurnover")}</Text>
                        <SideRow label={t("inbound:damaged.detail.avgDaysBucket")} value={
                          currentBucketStat.activeRows > 0 ? t("inbound:damaged.detail.days", { days: currentBucketStat.avgDaysInBucket }) : null
                        } mono />
                        <SideRow label={t("inbound:damaged.detail.avgDaysDispose")} value={
                          currentBucketStat.disposedRows > 0 ? t("inbound:damaged.detail.days", { days: currentBucketStat.avgDaysToDispose }) : null
                        } mono />
                        <SideRow label={t("inbound:damaged.detail.disposedOfTotal")} value={
                          `${currentBucketStat.disposedRows} / ${currentBucketStat.activeRows + currentBucketStat.disposedRows}`
                        } mono />
                      </Stack>
                    ) : null}
                  </Stack>
                ) : (
                  <Skeleton h={140} />
                )}
              </DetailCard>
            </Grid.Col>
          </Grid>
        ) : null}
      </Stack>

      {/* ── Dispose modal ── */}
      <Modal opened={disposeOpen} onClose={disposeH.close} title={t("inbound:damaged.detail.disposeTitle")} size={440}>
        <Stack gap="md">
          <Select
            label={<>{t("inbound:damaged.list.dispositionLabel")}<HelpIcon helpKey={`${HELP_LIST}.types.disposition`} /></>}
            data={dispositionOptions(t)}
            value={disposition}
            onChange={(v) => v && setDisposition(v as FinalDisposition)}
            allowDeselect={false}
            required
          />
          <Textarea label={t("inbound:damaged.detail.notes")} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} />
          <ConfirmNote helpKey={`${HELP}.confirm.dispose`} mt={0} />
          <Group justify="flex-end">
            <Button variant="default" onClick={disposeH.close}>{t("inbound:damaged.detail.close")}</Button>
            <Button onClick={confirmDispose} loading={busy}>{t("inbound:damaged.detail.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Category change modal ── */}
      <Modal opened={moveOpen} onClose={moveH.close} title={t("inbound:damaged.detail.moveTitle")} size={440}>
        <Stack gap="md">
          <Text size="sm" c="var(--nw-text-2)">{t("inbound:damaged.detail.moveBody")}</Text>
          <Textarea label={t("inbound:damaged.detail.notesOptional")} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} />
          <ConfirmNote helpKey={`${HELP}.confirm.move`} mt={0} />
          <Group justify="flex-end">
            <Button variant="default" onClick={moveH.close}>{t("inbound:damaged.detail.close")}</Button>
            <Button onClick={confirmMove} loading={busy}>{t("inbound:damaged.detail.move")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Photo zoom ── */}
      <Modal opened={galleryOpen} onClose={galleryH.close} size="lg" centered
        title={<Group gap="xs"><ImageIcon size={18} /><Text fw={600}>{t("inbound:damaged.detail.photoEvidence")}</Text></Group>}>
        {gallery.length ? (
          <Stack gap="sm">
            <Image src={gallery[gIdx]} radius="md" mah={440} fit="contain" fallbackSrc="https://placehold.co/600x400?text=No+image" />
            {gallery.length > 1 ? (
              <Group justify="center" gap="md">
                <ActionIcon variant="default" onClick={gPrev} aria-label={t("inbound:damaged.detail.prev")}><CaretLeftIcon size={18} /></ActionIcon>
                <Text size="sm">{gIdx + 1} / {gallery.length}</Text>
                <ActionIcon variant="default" onClick={gNext} aria-label={t("inbound:damaged.detail.next")}><CaretRightIcon size={18} /></ActionIcon>
              </Group>
            ) : null}
          </Stack>
        ) : null}
      </Modal>
    </PageLayout>
  );
}
